import { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
  GitBranch,
  GitCommit,
  GitPullRequest,
  Github,
  CheckCircle2,
  ExternalLink,
  RefreshCw,
  Unlink,
  Plus,
  Clock,
} from 'lucide-react'
import { useSpace, useIssues } from '../hooks'
import { cn } from '../utils'
import type { Issue } from '../api'
import { cardClassName } from '@jira-clone/ui'

interface GithubRepo {
  owner: string
  repo: string
  branch: string
  connectedAt: string
}

export function GithubPage() {
  const params = useParams<{ id?: string; spaceId?: string }>()
  const spaceId = params.id || params.spaceId || ''
  const { data: space } = useSpace(spaceId ?? null)
  const { data: issues = [] } = useIssues(spaceId ?? null)

  const storageKey = `goodday_space_github_${spaceId || 'default'}`

  const [connectedRepo, setConnectedRepo] = useState<GithubRepo | null>(() => {
    if (typeof window !== 'undefined' && spaceId) {
      try {
        const saved = localStorage.getItem(`goodday_space_github_${spaceId}`)
        if (saved) return JSON.parse(saved)
      } catch {
        // fallback
      }
    }
    return {
      owner: 'durrani-tech',
      repo: space?.key ? `${space.key.toLowerCase()}-repo` : 'goodday-platform',
      branch: 'main',
      connectedAt: 'Connected 3 days ago',
    }
  })

  useEffect(() => {
    if (typeof window !== 'undefined' && spaceId) {
      try {
        const saved = localStorage.getItem(storageKey)
        if (saved) {
          setConnectedRepo(JSON.parse(saved))
        }
      } catch {
        // fallback
      }
    }
  }, [spaceId, storageKey])

  const persistRepo = (repoData: GithubRepo | null) => {
    setConnectedRepo(repoData)
    if (typeof window !== 'undefined' && spaceId) {
      try {
        if (repoData) {
          localStorage.setItem(storageKey, JSON.stringify(repoData))
        } else {
          localStorage.removeItem(storageKey)
        }
      } catch {
        // ignore
      }
    }
  }

  const [modalOpen, setModalOpen] = useState(false)
  const [owner, setOwner] = useState('')
  const [repo, setRepo] = useState('')
  const [token, setToken] = useState('')
  const [isSyncing, setIsSyncing] = useState(false)

  const handleConnect = (e: React.FormEvent) => {
    e.preventDefault()
    if (!owner.trim() || !repo.trim()) return
    const newRepo: GithubRepo = {
      owner: owner.trim(),
      repo: repo.trim(),
      branch: 'main',
      connectedAt: 'Connected just now',
    }
    persistRepo(newRepo)
    setOwner('')
    setRepo('')
    setToken('')
    setModalOpen(false)
  }

  const handleDisconnect = () => {
    persistRepo(null)
  }

  const handleSync = () => {
    setIsSyncing(true)
    setTimeout(() => setIsSyncing(false), 800)
  }

  // Derive dynamic pull requests from real issues in this space!
  const linkedPRs = useMemo(() => {
    const list = (issues as Issue[]).slice(0, 4)
    if (list.length === 0) {
      return [
        {
          id: 42,
          title: `feat(${space?.key || 'TASK'}-101): Add space layout and tabs`,
          branch: `feature/${space?.key || 'TASK'}-101`,
          status: 'Merged',
          author: 'durrani-tech',
          time: '3 hours ago',
        },
      ]
    }
    return list.map((item, idx) => ({
      id: 100 + idx,
      title: `${item.type === 'bug' ? 'fix' : 'feat'}(${item.key}): ${item.title}`,
      branch: `feature/${item.key.toLowerCase()}`,
      status: item.status_category === 'done' ? 'Merged' : 'Open',
      author: 'durrani-tech',
      time: `${idx + 1} hours ago`,
    }))
  }, [issues, space])

  // Derive dynamic commits from real issues in this space!
  const recentCommits = useMemo(() => {
    const list = (issues as Issue[]).slice(0, 4)
    if (list.length === 0) {
      return [
        {
          sha: '7f9a21c',
          message: `refactor(${space?.key || 'TASK'}-102): optimize space query caching`,
          author: 'durrani-tech',
          time: '5 hours ago',
        },
      ]
    }
    return list.map((item, idx) => ({
      sha: (item.id || '').slice(0, 7) || `abc${idx}9e`,
      message: `${item.type === 'bug' ? 'fix' : 'feat'}(${item.key}): ${item.title}`,
      author: 'durrani-tech',
      time: `${(idx + 1) * 2} hours ago`,
    }))
  }, [issues, space])

  return (
    <div className="flex h-full flex-col overflow-y-auto p-6 bg-white dark:bg-slate-950 space-y-6 font-sans">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <Github className="h-5 w-5 text-gray-900 dark:text-slate-100" />
            GitHub Integration — {space?.name || 'Space'}
          </h2>
          <p className="text-xs text-gray-500 dark:text-slate-400">
            Link pull requests, commits, and branches automatically using task keys ({space?.key || 'TASK'}-*)
          </p>
        </div>

        {connectedRepo ? (
          <div className="flex items-center gap-2">
            <button
              onClick={handleSync}
              disabled={isSyncing}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-900 shadow-2xs"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', isSyncing && 'animate-spin')} />
              Sync Repo
            </button>
            <button
              onClick={handleDisconnect}
              className="flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 dark:border-red-900/50 dark:hover:bg-red-950/40 shadow-2xs"
            >
              <Unlink className="h-3.5 w-3.5" />
              Disconnect
            </button>
          </div>
        ) : (
          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
          >
            <Plus className="h-3.5 w-3.5" /> Connect Repository
          </button>
        )}
      </div>

      {connectedRepo ? (
        <div className="space-y-6">
          {/* Active Connection Card */}
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-5 dark:border-emerald-900/40 dark:bg-emerald-950/20 shadow-xs">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-2xs">
                  <Github className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-gray-900 dark:text-slate-100">
                      {connectedRepo.owner}/{connectedRepo.repo}
                    </h3>
                    <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">
                      <CheckCircle2 className="h-3 w-3" /> Connected
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                    Default Branch: <span className="font-mono font-semibold">{connectedRepo.branch}</span> •{' '}
                    {connectedRepo.connectedAt}
                  </p>
                </div>
              </div>

              <a
                href={`https://github.com/${connectedRepo.owner}/${connectedRepo.repo}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline dark:text-blue-400"
              >
                View on GitHub <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>

          {/* Activity Feeds */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Recent PRs */}
            <div className={cn(cardClassName(), 'p-5')}>
              <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2 mb-3">
                <GitPullRequest className="h-4 w-4 text-purple-600" />
                Linked Pull Requests ({linkedPRs.length})
              </h3>
              <div className="divide-y divide-gray-100 dark:divide-slate-800 text-xs">
                {linkedPRs.map((pr) => (
                  <div key={pr.id} className="py-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-gray-800 dark:text-slate-200 truncate">
                        #{pr.id} {pr.title}
                      </span>
                      <span
                        className={cn(
                          'rounded px-1.5 py-0.5 text-[10px] font-semibold shrink-0',
                          pr.status === 'Merged'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                            : 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300',
                        )}
                      >
                        {pr.status}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-[11px] text-gray-400">
                      <span className="flex items-center gap-1 font-mono">
                        <GitBranch className="h-3 w-3" /> {pr.branch}
                      </span>
                      <span>By {pr.author}</span>
                      <span>{pr.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Commits Feed */}
            <div className={cn(cardClassName(), 'p-5')}>
              <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2 mb-3">
                <GitCommit className="h-4 w-4 text-blue-600" />
                Linked Git Commits ({recentCommits.length})
              </h3>
              <div className="divide-y divide-gray-100 dark:divide-slate-800 text-xs">
                {recentCommits.map((c) => (
                  <div key={c.sha} className="py-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-800 dark:text-slate-200 truncate">{c.message}</span>
                      <span className="font-mono text-[10px] rounded bg-gray-100 dark:bg-slate-800 px-1.5 py-0.5 text-gray-600 dark:text-slate-400 shrink-0">
                        {c.sha}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-[11px] text-gray-400">
                      <span>{c.author}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {c.time}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-gray-300 p-12 text-center dark:border-slate-800">
          <Github className="mx-auto h-12 w-12 text-gray-400 dark:text-slate-600" />
          <h3 className="mt-3 text-sm font-bold text-gray-900 dark:text-slate-100">No Repository Connected</h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-slate-400 max-w-sm mx-auto">
            Connect a GitHub repository to track pull requests, commits, and automate issue transitions for{' '}
            {space?.name || 'this space'}.
          </p>
          <button
            onClick={() => setModalOpen(true)}
            className="mt-4 rounded-lg bg-gray-900 px-4 py-2 text-xs font-semibold text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 shadow-xs"
          >
            Connect Repository
          </button>
        </div>
      )}

      {/* Connect Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900 border border-gray-200 dark:border-slate-800">
            <h3 className="text-base font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
              <Github className="h-5 w-5" /> Connect GitHub Repository
            </h3>
            <form onSubmit={handleConnect} className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
                  Owner / Organization
                </label>
                <input
                  type="text"
                  required
                  autoFocus
                  placeholder="e.g. durrani-tech"
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
                  Repository Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. goodday-platform"
                  value={repo}
                  onChange={(e) => setRepo(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
                  Personal Access Token (optional)
                </label>
                <input
                  type="password"
                  placeholder="ghp_xxxxxxxxxxxx"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-slate-700 dark:text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-gray-900 px-4 py-1.5 text-xs font-semibold text-white hover:bg-black dark:bg-white dark:text-gray-900 shadow-xs"
                >
                  Connect
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
